/**
 * Состояние графика на странице: вид с сервера, правки с мгновенным пересчётом в браузере,
 * отправка пакетом, отмена и повтор (Ctrl+Z, Ctrl+Y). Если сервер правку не принял — вид
 * возвращается к последнему подтверждённому, человек видит причину.
 */
import { mergeScheduleDelta, type ScheduleDelta, type ScheduleOp, type ScheduleView } from '@epc/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { toast } from '../../ui/feedback';
import { applyLocal, inverseOps, recalcLocal, type View } from './model';

export const scheduleKey = (projectId: string) => ['schedule', projectId] as const;

export function scheduleQuery(projectId: string) {
  return {
    queryKey: scheduleKey(projectId),
    queryFn: async (): Promise<ScheduleView | null> => {
      try {
        return await api<ScheduleView>(`/projects/${projectId}/schedule`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404 && /не создан/.test(e.message)) return null;
        throw e;
      }
    },
  };
}

interface Step {
  label: string;
  ops: ScheduleOp[];
  inverse: ScheduleOp[];
}

export function useSchedule(projectId: string) {
  const qc = useQueryClient();
  const q = useQuery(scheduleQuery(projectId));
  const [local, setLocal] = useState<View | null>(null);
  const [pending, setPending] = useState(0);
  const undo = useRef<Step[]>([]);
  const redo = useRef<Step[]>([]);
  const [, bump] = useState(0);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const inflight = useRef(0);

  const view = local ?? q.data ?? null;
  const viewRef = useRef(view);
  viewRef.current = view;

  const setServer = useCallback(
    (v: ScheduleView) => {
      qc.setQueryData(scheduleKey(projectId), v);
      setLocal(null);
    },
    [qc, projectId],
  );

  /** Отправить пакет; правки идут по очереди, чтобы не обогнать друг друга */
  const send = useCallback(
    (ops: ScheduleOp[], label?: string) => {
      const v = viewRef.current;
      if (!v) return Promise.resolve();
      setPending((n) => n + 1);
      inflight.current++;
      const revision = v.schedule.revision;
      const p = chain.current.then(() =>
        api<ScheduleView | ScheduleDelta>(`/schedules/${v.schedule.id}/changes`, {
          // delta: сервер вернёт только изменённые работы и связи, если видел ту же версию графика (ПМ-08)
          body: { ops, revision, label: label?.slice(0, 200), delta: true },
        })
          .then(async (answer) => {
            let res: ScheduleView;
            if ('delta' in answer) {
              // Изменённое ложится на подтверждённый вид той версии, на которой сделана правка
              const base = qc.getQueryData<ScheduleView | null>(scheduleKey(projectId));
              if (!base || base.schedule.revision !== revision) {
                // Вид на странице другой версии: перечитать график целиком
                await qc.refetchQueries({ queryKey: scheduleKey(projectId) });
                if (inflight.current === 1) setLocal(null);
                return;
              }
              res = mergeScheduleDelta(base, answer);
            } else res = answer;
            // Пока в очереди есть ещё правки — показываем свой расчёт, ответ сервера кладём в запас
            if (inflight.current === 1) setServer(res);
            else qc.setQueryData(scheduleKey(projectId), res);
          })
          .catch((e: unknown) => {
            toast(e instanceof Error ? e.message : 'Правка не сохранена', 'danger');
            setLocal(null);
            undo.current = [];
            redo.current = [];
            qc.invalidateQueries({ queryKey: scheduleKey(projectId) });
            throw e;
          })
          .finally(() => {
            inflight.current--;
            setPending((n) => n - 1);
          }),
      );
      chain.current = p.catch(() => undefined);
      return p;
    },
    [setServer, qc, projectId],
  );

  /** Правка: сразу видна с пересчитанными датами, затем подтверждается сервером */
  const commit = useCallback(
    (ops: ScheduleOp[], label: string) => {
      const v = viewRef.current;
      if (!v || !ops.length) return Promise.resolve();
      const inverse = inverseOps(v, ops);
      undo.current.push({ label, ops, inverse });
      if (undo.current.length > 100) undo.current.shift();
      redo.current = [];
      bump((x) => x + 1);
      setLocal(recalcLocal(applyLocal(v, ops)));
      return send(ops, label);
    },
    [send],
  );

  const doUndo = useCallback(() => {
    const step = undo.current.pop();
    const v = viewRef.current;
    if (!step || !v) return;
    redo.current.push(step);
    bump((x) => x + 1);
    setLocal(recalcLocal(applyLocal(v, step.inverse)));
    toast(`Отменено: ${step.label}`, 'info');
    send(step.inverse, `Отмена: ${step.label}`);
  }, [send]);

  const doRedo = useCallback(() => {
    const step = redo.current.pop();
    const v = viewRef.current;
    if (!step || !v) return;
    undo.current.push(step);
    bump((x) => x + 1);
    setLocal(recalcLocal(applyLocal(v, step.ops)));
    toast(`Повторено: ${step.label}`, 'info');
    send(step.ops, `Повтор: ${step.label}`);
  }, [send]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)
      )
        return;
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if ((k === 'z' || k === 'я') && !e.shiftKey) {
        e.preventDefault();
        doUndo();
      } else if (k === 'y' || k === 'н' || ((k === 'z' || k === 'я') && e.shiftKey)) {
        e.preventDefault();
        doRedo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [doUndo, doRedo]);

  return {
    q,
    view,
    pending: pending > 0,
    commit,
    setServer,
    undo: doUndo,
    redo: doRedo,
    undoLabel: undo.current.at(-1)?.label ?? null,
    redoLabel: redo.current.at(-1)?.label ?? null,
  };
}
